import { assetUrl } from '~/utils/assetUrl'

/**
 * 👹 Mission-enemy art (v1.19). These are the idle loops that will pop up
 * on the field for combat missions — NOT creatures (they never enter the
 * roster, the codex or the shop), so they live in their own registry
 * instead of CREATURE_ART. Approved via /approve-creature but promoted by
 * hand into public/enemies/ because the creature-approve path would have
 * mis-registered them.
 *
 * Keyed by the enemy id (matches art/ASSET_WISHLIST.md §10b). Only the
 * fire set is animated so far; the on-field display reads this registry
 * and falls back to the mission card when an enemy has no art yet.
 *
 * ☁️ v7.84: site paths (the art host's bucket keys); enemyArt() and
 * enemyState() hand out the URLs to render, through assetUrl().
 */
export const ENEMY_ART: Record<string, string> = {
  'ash-crawlers': '/enemies/ash-crawlers.webp',
  'blaze-beetles': '/enemies/blaze-beetles.webp',
  'cinder-hounds': '/enemies/cinder-hounds.webp',
  'ember-imps': '/enemies/ember-imps.webp',
  'kiln-spirits': '/enemies/kiln-spirits.webp',
  'scorch-rats': '/enemies/scorch-rats.webp',
  'wildfire-swarm': '/enemies/wildfire-swarm.webp',
  // 👹 v2.12 batch (Javi's 39 new paintings): base is a keyed STILL
  // facing left; victory/defeat loops rendered per enemy. Singular
  // ids since v2.14 — the mission names went singular to match the
  // single-creature paintings (fire keeps its group paintings).
  'flare-wisps': '/enemies/flare-wisps.webp',
  // Late arrivals (v2.14): Puddle Slimes stays PLURAL (painted as two).
  'puddle-slimes': '/enemies/puddle-slimes.webp',
  'vine-creeper': '/enemies/vine-creeper.webp',
  'dew-drake': '/enemies/dew-drake.webp',
  'mist-crab': '/enemies/mist-crab.webp',
  'tide-sprite': '/enemies/tide-sprite.webp',
  'brine-eel': '/enemies/brine-eel.webp',
  'rain-wraith': '/enemies/rain-wraith.webp',
  'foam-serpent': '/enemies/foam-serpent.webp',
  'drowned-roots': '/enemies/drowned-roots.webp',
  'overgrown-slime': '/enemies/overgrown-slime.webp',
  'bramble-beetle': '/enemies/bramble-beetle.webp',
  'moss-golem': '/enemies/moss-golem.webp',
  'thorn-hare': '/enemies/thorn-hare.webp',
  'spore-puff': '/enemies/spore-puff.webp',
  'nettle-imp': '/enemies/nettle-imp.webp',
  'creeping-canopy': '/enemies/creeping-canopy.webp',
  'static-sprite': '/enemies/static-sprite.webp',
  'volt-mite': '/enemies/volt-mite.webp',
  'storm-hawk': '/enemies/storm-hawk.webp',
  'spark-jackal': '/enemies/spark-jackal.webp',
  'arc-wisp': '/enemies/arc-wisp.webp',
  'thunder-grub': '/enemies/thunder-grub.webp',
  'flash-fiend': '/enemies/flash-fiend.webp',
  'charged-vortex': '/enemies/charged-vortex.webp',
  'pebble-gnome': '/enemies/pebble-gnome.webp',
  'mud-golem': '/enemies/mud-golem.webp',
  'boulder-beetle': '/enemies/boulder-beetle.webp',
  'dust-digger': '/enemies/dust-digger.webp',
  'clay-shambler': '/enemies/clay-shambler.webp',
  'gravel-wolf': '/enemies/gravel-wolf.webp',
  'sandstone-serpent': '/enemies/sandstone-serpent.webp',
  'rumbling-cairn': '/enemies/rumbling-cairn.webp',
  // Boss-tier flavor names ("The Ruin Colossus"...) — shown when a
  // boss-tier mission has no named map boss claiming it.
  'the-ruin-colossus': '/enemies/the-ruin-colossus.webp',
  'tempest-leviathan': '/enemies/tempest-leviathan.webp',
  'the-verdant-maw': '/enemies/the-verdant-maw.webp',
  'magma-behemoth': '/enemies/magma-behemoth.webp',
  'the-hollow-titan': '/enemies/the-hollow-titan.webp',
  'stormcrown-wyrm': '/enemies/stormcrown-wyrm.webp',
  'the-sunken-king': '/enemies/the-sunken-king.webp',
  'avalanche-alpha': '/enemies/avalanche-alpha.webp',
}

/** 🔄 Per-enemy art revision — bumped whenever an id's files are edited
 * IN PLACE, applied to idle AND both states (the day-long asset cache
 * ghosts stale copies otherwise). v4.16: Javi's face-off review — these
 * sixteen were painted facing right and got mirrored to look LEFT at
 * the team on the resolution card. */
const ENEMY_REV: Record<string, string> = Object.fromEntries([
  'ash-crawlers', 'blaze-beetles', 'brine-eel', 'cinder-hounds',
  'dew-drake', 'drowned-roots', 'ember-imps', 'flare-wisps',
  'foam-serpent', 'kiln-spirits', 'magma-behemoth', 'mist-crab',
  'puddle-slimes', 'scorch-rats', 'the-ruin-colossus', 'wildfire-swarm',
  // v4.57: the rain wraith joined the mirror list (Javi's review).
  'rain-wraith',
].map(id => [id, '3']))
// 🎭 v7.33 (Javi's review): victory + defeat re-rendered on hydra from
// padded anchors - the drake grew wings past the frame, the colossus had
// blocks keyed to glass. Idles untouched, but the rev rides all three.
ENEMY_REV['dew-drake'] = '4'
ENEMY_REV['the-ruin-colossus'] = '4'
// 🔥 v7.46 (per Javi: "it looks pixely"): the still re-keyed from Javi's
// source with the soft magenta matte (the v3.24 keyer repair had left
// blocky halos and specks), victory + defeat re-rendered on hydra from it.
ENEMY_REV['flare-wisps'] = '4'

// 🍃 v4.90 asset diet: enemy loops re-encoded at 320px in place.
const diet = (url: string) => `${url}${url.includes('?') ? '&' : '?'}d=1`

/** The enemy loop for an id, or null when it hasn't been animated yet. */
export function enemyArt(id: string): string | null {
  const base = ENEMY_ART[id]
  if (!base) return null
  const rev = ENEMY_REV[id]
  return assetUrl(diet(rev ? `${base}?v=${rev}` : base))
}

/** 🎭 Reaction loops (v1.64, Javi-approved batch): every painted enemy
 * has a triumphant gloat ('victory') and a defeated collapse ('defeat')
 * beside its idle — shown on the resolution card once the wheel lands. */
export function enemyState(id: string, state: 'victory' | 'defeat'): string | null {
  if (!ENEMY_ART[id]) return null
  // 🧊 defeat ?v=2 (v4.15): loop-patched to freeze on the last frame.
  // A per-id rev (flips) supersedes it — always the higher number.
  const rev = ENEMY_REV[id] ?? (state === 'defeat' ? '2' : null)
  return assetUrl(diet(`/enemies/${id}-${state}.webp${rev ? `?v=${rev}` : ''}`))
}

/* (Pack display tried v2.10–v2.13 and RETIRED in v2.14, per Javi: the
 * copy-stack never looked right — the mission names went singular
 * instead, matching the single-creature paintings.) */
